/**
 * useDismissable
 *
 * Closes an open popover on an outside mousedown or on Escape.
 *
 * @module hooks/useDismissable
 */

import { useEffect, type RefObject } from "react";

export const useDismissable = (
  ref: RefObject<HTMLElement | null>,
  isOpen: boolean,
  onDismiss: () => void
): void => {
  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: MouseEvent): void => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        onDismiss();
      }
    };

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onDismiss();
    };

    // mousedown, not click: dismiss before the underlying control receives its
    // own click, so a single press on something behind the popover closes it
    // rather than being swallowed.
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [ref, isOpen, onDismiss]);
};

export default useDismissable;
